import { isObjectScopedRole } from '@technic/contracts';
import { objectScopeAnswers } from '@shared/lib';
import { useAuth } from './authContext';

/**
 * Область объектной роли на портале (ADR 0039): объектов у учётки набор, а не один.
 *
 * Живёт в слайсе сессии, потому что читает `useAuth`: этим закрыты все прочие адреса, и почему —
 * сказано один раз, в шапке `index.ts` слайса. Здесь важно следствие: хук спрашивают и `features`
 * (подбор заказчика заявки), и `pages` (заказ ТС, оргтехника, гараж), так что «поближе к модулю»
 * означало бы либо импорт соседа по слою, либо вторую копию оси.
 *
 * `authContext` берётся прямым путём, а не через `@entities/session`: публичный вход слайса выводит
 * этот хук, и обращение к входу изнутри замкнуло бы барель на себя. Так же устроены и соседние оси.
 *
 * Роль здесь не перечисляется, спрашивается предикат `isObjectScopedRole` — заказчиков со стороны
 * объекта двое (ADR 0031), и оставленный список ролей разошёлся бы с `OBJECT_SCOPED_ROLES` молча,
 * фильтром объекта, открытым для чужих площадок.
 *
 * Хук считает ось, а ответы формы по ней собирает общий `objectScopeAnswers`: они у обеих осей
 * портала одни и те же, и второе их написание разошлось бы с первым на первой же правке. Роль без
 * этой оси передаёт туда `null` — «областью не ограничен», а не «объектов ноль»: пустой набор
 * оставил бы человека без единой строки в списке.
 *
 * Портал сужает выбор, но не решает доступ: чужой объект сервер всё равно отдаёт как 403, а список
 * — как пустую выборку. Замок у каждого модуля свой: заказ ТС закрывают `assertRequestScope` и
 * `vehicleRequestVisibilityWhere`, заявку оргтехники — `assertServiceRequestVisible` и
 * `serviceRequestVisibilityWhere`. Площадочную пару (`assertPlaceObjectScope`,
 * `placeObjectVisibilityWhere`) сюда подставлять нельзя: она сторожит вторую ось
 * (`usePlaceObjectScope`) и зовётся только из вывоза мусора и механизации, а этот хук спрашивают
 * заказ ТС, оргтехника и гараж — имя существовало бы, но указывало на замок соседней двери.
 */
export function useObjectScope() {
  const { user } = useAuth();
  const isObjectRole = isObjectScopedRole(user?.role);
  return {
    isObjectRole,
    ...objectScopeAnswers(isObjectRole ? (user?.constructionObjectIds ?? []) : null),
  };
}
